import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getBodySites, getConstraintSets, createConstraintSet, exportBulkXlsx, getReviewsDue,
  getDashboard, listMyAnnualReviewAssignments,
} from '../api/endpoints';
import type { DashboardBundle } from '../api/endpoints';
import { useToast } from '../components/Toast';
import AssignReviewModal from '../components/AssignReviewModal';
import { peekCachedFetch, primeCachedFetch } from '../hooks/useCachedFetch';
import { downloadBlob } from '../utils/download';
import type {
  AnnualReviewAssignment, BodySite, ConstraintSetList, ReviewsDueResponse, User,
} from '../types';
import type { OneOffSet } from '../types/oneoffs';

interface Props {
  user: User;
}

// SPA-session cache of the last aggregator response (shared store from
// useCachedFetch, so logout wipes it together with the analytics /
// consistency caches). Revisiting the dashboard renders the cached bundle
// instantly (no blocking spinner) while loadData() refreshes in the
// background — part of the "every page change takes 1-2 s" fix. The
// dashboard uses peek/prime instead of the useCachedFetch hook because
// loadData() is also called imperatively after mutations (create set,
// reaffirm) — don't "unify" this onto the hook blindly.
const DASHBOARD_CACHE_KEY = 'dashboard-bundle';

type SortField = 'name' | 'status';
type SortDir = 'asc' | 'desc';

function getStatusRank(cs: ConstraintSetList): number {
  if (cs.has_pending) return 1;
  if (cs.has_draft) return 2;
  if (cs.active_version_number) return 3;
  if (!cs.is_active) return 5;
  return 4;
}

export default function DashboardPage({ user }: Props) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  // Read once for the initial-state seeds below (initializers only run on
  // first render).
  const dashboardCache = peekCachedFetch<DashboardBundle>(DASHBOARD_CACHE_KEY) ?? null;
  const [bodySites, setBodySites] = useState<BodySite[]>(
    dashboardCache?.body_sites ?? [],
  );
  const [constraintSets, setConstraintSets] = useState<ConstraintSetList[]>(
    dashboardCache?.constraint_sets ?? [],
  );
  const [selectedSite, setSelectedSite] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'draft' | 'pending'>('all');
  const [showDeprecated, setShowDeprecated] = useState(false);
  // No blocking spinner when a cached bundle exists — render it and
  // refresh in the background.
  const [loading, setLoading] = useState(dashboardCache === null);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [selectedForExport, setSelectedForExport] = useState<Set<number>>(new Set());
  const [exporting, setExporting] = useState(false);
  const [reviewsDue, setReviewsDue] = useState<ReviewsDueResponse | null>(
    dashboardCache?.reviews_due ?? null,
  );
  const [reviewsDueLoading, setReviewsDueLoading] = useState(dashboardCache === null);
  const [reviewsDueError, setReviewsDueError] = useState(false);
  const [assignReviewOpen, setAssignReviewOpen] = useState(false);
  const [myAssignments, setMyAssignments] = useState<AnnualReviewAssignment[]>([]);
  // v0.6.0.17: surface OneOffSets per body site on the dashboard so
  // clinicians find their saved sets from the front page (not just
  // /one-offs). Fed by the aggregator's new `one_off_sets` field;
  // empty array tolerates older deploys where the field is absent.
  const [oneOffSets, setOneOffSets] = useState<OneOffSet[]>(
    dashboardCache?.one_off_sets ?? [],
  );

  const loadMyAssignments = () => {
    listMyAnnualReviewAssignments({ pending: true })
      .then((res) => {
        // Endpoint returns either {results: []} (paginated) or [] (unpaginated).
        const data = res.data;
        const list = Array.isArray(data) ? data : (data.results ?? []);
        setMyAssignments(list);
      })
      .catch(() => {
        // Non-blocking — silently swallow; the assignments widget is
        // informational, not a critical path.
      });
  };
  useEffect(() => {
    loadMyAssignments();
  }, []);
  const [newSet, setNewSet] = useState({
    body_site: 0,
    name: '',
    location: '',
    fractions: 1,
    description: '',
  });

  // Primary path: hit the v0.6.0.6 dashboard aggregator, which returns
  // body_sites + constraint_sets + reviews_due in one round trip. Saves
  // ~300-400ms of HTTP overhead vs the three legacy calls on a cold load.
  const loadDataAggregated = () => {
    setReviewsDueLoading(true);
    setReviewsDueError(false);
    return getDashboard()
      .then((res) => {
        // primeCachedFetch is epoch-unaware, but the dashboard only renders
        // for the logged-in user, so a post-logout resolve can't be seen —
        // and login wipes the cache again anyway.
        primeCachedFetch(DASHBOARD_CACHE_KEY, res.data);
        setBodySites(res.data.body_sites);
        setConstraintSets(res.data.constraint_sets);
        setReviewsDue(res.data.reviews_due);
        // v0.6.0.17: tolerate older deploys that don't return
        // `one_off_sets` — empty array keeps the panel hidden.
        setOneOffSets(res.data.one_off_sets ?? []);
        if (res.data.body_sites.length > 0 && newSet.body_site === 0) {
          setNewSet((prev) => ({ ...prev, body_site: res.data.body_sites[0].id }));
        }
      })
      .finally(() => setReviewsDueLoading(false));
  };

  // Fallback path: if the aggregator 404s (older deploy) or otherwise fails,
  // drop back to the three individual endpoints. Both paths stay live so
  // there is no migration window.
  const loadDataLegacy = () => {
    setReviewsDueLoading(true);
    setReviewsDueError(false);
    const sitesAndSets = Promise.all([getBodySites(), getConstraintSets()])
      .then(([sitesRes, setsRes]) => {
        setBodySites(sitesRes.data.results);
        setConstraintSets(setsRes.data.results);
        if (sitesRes.data.results.length > 0 && newSet.body_site === 0) {
          setNewSet((prev) => ({ ...prev, body_site: sitesRes.data.results[0].id }));
        }
      });
    const reviews = getReviewsDue('all')
      .then((res) => setReviewsDue(res.data))
      .catch(() => setReviewsDueError(true))
      .finally(() => setReviewsDueLoading(false));
    return Promise.all([sitesAndSets, reviews]);
  };

  // loadData kept as the public entry point used by handleCreate after a
  // new ConstraintSet is created — it just refetches everything via the
  // fastest available path.
  //
  // Fallback gating: ONLY fall back to the 3 legacy calls if the aggregator
  // 404s (older deploy without /api/dashboard/). On 401/500/timeout the
  // legacy endpoints will hit the same error, so doubling the work just
  // doubles the user's wait and the backend log noise (adversarial review H3).
  const loadData = () =>
    loadDataAggregated().catch((err: any) => {
      if (err?.response?.status === 404) {
        return loadDataLegacy();
      }
      throw err;
    });

  // Exposed for the ReviewsDueWidget's Retry button — only re-fetches the
  // reviews-due slice (no body_sites / constraint_sets refresh needed).
  const loadReviewsDue = () => {
    setReviewsDueLoading(true);
    setReviewsDueError(false);
    return getReviewsDue('all')
      .then((res) => setReviewsDue(res.data))
      .catch(() => setReviewsDueError(true))
      .finally(() => setReviewsDueLoading(false));
  };

  useEffect(() => {
    loadData().finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSet.name.trim()) return;
    setCreating(true);
    try {
      const res = await createConstraintSet({
        ...newSet,
        location: newSet.location || bodySites.find((s) => s.id === newSet.body_site)?.name || '',
      });
      setShowCreate(false);
      setNewSet({ body_site: bodySites[0]?.id || 0, name: '', location: '', fractions: 1, description: '' });
      await loadData();
      navigate(`/constraint-sets/${res.data.id}`);
    } catch (err: any) {
      showToast(err.response?.data?.name?.[0] || err.response?.data?.error || 'Failed to create constraint set.', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortIndicator = (field: SortField) => {
    if (sortField !== field) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  };

  const toggleExportSelect = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedForExport((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleExportSelected = async () => {
    if (selectedForExport.size === 0) return;
    setExporting(true);
    try {
      const res = await exportBulkXlsx(Array.from(selectedForExport));
      downloadBlob(res.data, 'constraint_sets_export.xlsx');
      setSelectedForExport(new Set());
    } catch {
      showToast('Export failed.', 'error');
    } finally {
      setExporting(false);
    }
  };

  const filtered = useMemo(() => {
    return constraintSets.filter((cs) => {
      if (!showDeprecated && !cs.is_active) return false;
      if (selectedSite !== null && cs.body_site !== selectedSite) return false;
      if (search && !cs.name.toLowerCase().includes(search.toLowerCase())) return false;
      if (statusFilter === 'active' && !cs.active_version_number) return false;
      if (statusFilter === 'draft' && !cs.has_draft) return false;
      if (statusFilter === 'pending' && !cs.has_pending) return false;
      return true;
    });
  }, [constraintSets, showDeprecated, selectedSite, search, statusFilter]);

  const sorted = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      let cmp = 0;
      if (sortField === 'name') {
        cmp = a.name.localeCompare(b.name);
      } else if (sortField === 'status') {
        cmp = getStatusRank(a) - getStatusRank(b);
      }
      return sortDir === 'desc' ? -cmp : cmp;
    });
    return items;
  }, [filtered, sortField, sortDir]);

  // Group by body site
  const grouped = useMemo(() => {
    const groups: Record<string, ConstraintSetList[]> = {};
    for (const cs of sorted) {
      const site = cs.body_site_name;
      if (!groups[site]) groups[site] = [];
      groups[site].push(cs);
    }
    return groups;
  }, [sorted]);

  // v0.6.0.17: per-body-site one-off-set count for the body-site grid
  // badge. O(N) over the flat list; cheap because one-off sets are
  // typically tens, not thousands.
  const oneOffCountBySite = useMemo(() => {
    const counts: Record<number, number> = {};
    for (const s of oneOffSets) {
      counts[s.body_site] = (counts[s.body_site] ?? 0) + 1;
    }
    return counts;
  }, [oneOffSets]);

  // OneOffSets filtered to the selected body site (when one is selected).
  // When no site is selected, show everything — same UX as the constraint
  // sets list. Sets are pre-sorted server-side by (body_site, name).
  const visibleOneOffSets = useMemo(() => {
    if (selectedSite === null) return oneOffSets;
    return oneOffSets.filter((s) => s.body_site === selectedSite);
  }, [oneOffSets, selectedSite]);

  if (loading) {
    return <div className="loading-spinner" style={{ margin: '48px auto' }} />;
  }

  const totalSets = constraintSets.length;
  const activeSets = constraintSets.filter((s) => s.active_version_number !== null).length;
  const pendingSets = constraintSets.filter((s) => s.has_pending).length;
  const draftSets = constraintSets.filter((s) => s.has_draft).length;
  const deprecatedCount = constraintSets.filter((s) => !s.is_active).length;

  const getAgingMonths = (cs: ConstraintSetList): number | null => {
    if (!cs.active_version_date) return null;
    const activeDate = new Date(cs.active_version_date);
    const now = new Date();
    const months = (now.getFullYear() - activeDate.getFullYear()) * 12
      + (now.getMonth() - activeDate.getMonth());
    return months >= 12 ? months : null;
  };

  const getReviewBadge = (cs: ConstraintSetList): React.ReactElement | null => {
    if (!cs.is_active || !cs.active_version_number) return null;
    if (cs.review_status === 'overdue') {
      const days = cs.days_overdue ?? 0;
      return (
        <span
          key="review"
          className="badge"
          style={{ background: 'var(--color-danger)', color: '#fff' }}
          title={`Annual reaffirmation overdue by ${days} day${days === 1 ? '' : 's'}.`}
        >
          Overdue {days}d
        </span>
      );
    }
    if (cs.review_status === 'due_soon') {
      const dueDate = cs.next_review_due ? cs.next_review_due.slice(0, 10) : '';
      return (
        <span
          key="review"
          className="badge"
          style={{ background: 'var(--color-warning, #f59e0b)', color: '#fff' }}
          title={`Annual reaffirmation due ${dueDate}.`}
        >
          Due soon
        </span>
      );
    }
    if (cs.review_status === 'never_reviewed') {
      return (
        <span
          key="review"
          className="badge"
          style={{ background: 'var(--color-text-secondary)', color: '#fff' }}
          title="No reaffirmation on record since activation."
        >
          Never reviewed
        </span>
      );
    }
    return null;
  };

  const getStatusBadges = (cs: ConstraintSetList) => {
    const badges = [];
    if (!cs.is_active) badges.push(<span key="deprecated" className="badge" style={{ background: '#6b7280', color: '#fff' }}>Deprecated</span>);
    if (cs.active_version_number) badges.push(<span key="active" className="badge badge-active">Active</span>);
    if (cs.has_draft) badges.push(<span key="draft" className="badge badge-draft">Draft</span>);
    if (cs.has_pending) badges.push(<span key="pending" className="badge badge-pending">Pending</span>);
    const reviewBadge = getReviewBadge(cs);
    if (reviewBadge) {
      badges.push(reviewBadge);
    } else {
      const agingMonths = getAgingMonths(cs);
      if (agingMonths !== null) {
        badges.push(
          <span
            key="aging"
            className="badge"
            style={{ background: 'var(--color-warning, #f59e0b)', color: '#fff' }}
            title={`Active version is ${agingMonths} months old — consider reviewing`}
          >
            ⚠ {agingMonths}mo
          </span>
        );
      }
    }
    if (badges.length === 0) badges.push(<span key="none" className="badge badge-draft">No version</span>);
    return <>{badges}</>;
  };

  const selectAllFiltered = () => {
    const ids = filtered.filter((cs) => cs.active_version_number).map((cs) => cs.id);
    setSelectedForExport(new Set(ids));
  };

  return (
    <div>
      <div className="dashboard-header">
        <h1>Dose Constraints Dashboard</h1>
        <p>Manage institutional dose constraint protocols across all treatment sites</p>
      </div>

      <div className="dashboard-stats">
        <div className="stat-card" onClick={() => setStatusFilter('all')} style={{ cursor: 'pointer', borderColor: statusFilter === 'all' ? 'var(--color-primary)' : undefined }}>
          <div className="stat-value">{totalSets}</div>
          <div className="stat-label">All Sets</div>
        </div>
        <div className="stat-card" onClick={() => setStatusFilter('active')} style={{ cursor: 'pointer', borderColor: statusFilter === 'active' ? 'var(--color-primary)' : undefined }}>
          <div className="stat-value" style={{ color: 'var(--color-success)' }}>{activeSets}</div>
          <div className="stat-label">Active</div>
        </div>
        <div className="stat-card" onClick={() => setStatusFilter('draft')} style={{ cursor: 'pointer', borderColor: statusFilter === 'draft' ? 'var(--color-primary)' : undefined }}>
          <div className="stat-value">{draftSets}</div>
          <div className="stat-label">Drafts</div>
        </div>
        <div className="stat-card" onClick={() => setStatusFilter('pending')} style={{ cursor: 'pointer', borderColor: statusFilter === 'pending' ? 'var(--color-primary)' : undefined }}>
          <div className="stat-value" style={{ color: pendingSets > 0 ? 'var(--color-warning)' : undefined }}>
            {pendingSets}
          </div>
          <div className="stat-label">Pending</div>
        </div>
        <ReviewsDueWidget
          state={reviewsDue}
          loading={reviewsDueLoading}
          error={reviewsDueError}
          onRetry={loadReviewsDue}
        />
      </div>

      {myAssignments.length > 0 && (
        // Assigned-to-me banner. Shows only when the user has at least one
        // pending annual-review assignment, with click-through to the
        // specific constraint set so they can reaffirm immediately.
        // v0.6.0.12.
        <div
          className="card"
          style={{
            marginBottom: 16,
            borderLeft: '4px solid var(--color-warning, #f59e0b)',
          }}
        >
          <div className="card-header" style={{ paddingBottom: 4 }}>
            <h2 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>
              📌 {myAssignments.length} annual review{myAssignments.length === 1 ? '' : 's'} assigned to you
            </h2>
          </div>
          <div style={{ padding: '4px 16px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {myAssignments.slice(0, 5).map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => navigate(`/constraint-sets/${a.constraint_set}`)}
                style={{
                  textAlign: 'left',
                  background: 'transparent',
                  border: 'none',
                  padding: '6px 8px',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: 13,
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <span>
                  <strong>{a.constraint_set_body_site_name}</strong> — {a.constraint_set_name} ({a.constraint_set_fractions} fx)
                  {a.notes ? (
                    <span style={{ color: 'var(--color-text-secondary)', marginLeft: 8 }}>
                      — "{a.notes.length > 60 ? a.notes.slice(0, 60) + '…' : a.notes}"
                    </span>
                  ) : null}
                </span>
                <span style={{ color: 'var(--color-text-secondary)', fontSize: 11, whiteSpace: 'nowrap' }}>
                  by {a.assigned_by_username}
                </span>
              </button>
            ))}
            {myAssignments.length > 5 && (
              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', padding: '4px 8px' }}>
                + {myAssignments.length - 5} more
              </div>
            )}
          </div>
        </div>
      )}

      <div className="body-site-grid">
        {bodySites.map((site) => (
          <div
            key={site.id}
            className="body-site-card"
            onClick={() => setSelectedSite(selectedSite === site.id ? null : site.id)}
            style={{
              borderColor: selectedSite === site.id ? 'var(--color-primary)' : undefined,
              background: selectedSite === site.id ? '#eff6ff' : undefined,
            }}
          >
            <h3>{site.name}</h3>
            <div className="set-count">
              {site.constraint_set_count} constraint set{site.constraint_set_count !== 1 ? 's' : ''}
            </div>
            {(oneOffCountBySite[site.id] ?? 0) > 0 && (
              <button
                type="button"
                className="set-count"
                onClick={(e) => {
                  e.stopPropagation(); // don't toggle the card's filter selection
                  navigate(`/one-offs?body_site=${site.id}`);
                }}
                title="View one-off sets for this body site"
                style={{
                  fontSize: 12, opacity: 0.75, background: 'none', border: 'none',
                  padding: 0, cursor: 'pointer', textAlign: 'left', color: 'inherit',
                  textDecoration: 'underline',
                }}
              >
                {oneOffCountBySite[site.id]} one-off set{oneOffCountBySite[site.id] !== 1 ? 's' : ''}
              </button>
            )}
          </div>
        ))}
      </div>

      {showCreate && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <h2>New Constraint Set</h2>
          </div>
          <div className="card-body">
            <form onSubmit={handleCreate} style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Body Site</label>
                <select
                  value={newSet.body_site}
                  onChange={(e) => setNewSet({ ...newSet, body_site: Number(e.target.value) })}
                >
                  {bodySites.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: 200 }}>
                <label>Name</label>
                <input
                  type="text"
                  value={newSet.name}
                  onChange={(e) => setNewSet({ ...newSet, name: e.target.value })}
                  placeholder="e.g., Prostate SBRT 5 fx"
                  required
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0, width: 100 }}>
                <label>Fractions</label>
                <input
                  type="number"
                  value={newSet.fractions}
                  onChange={(e) => setNewSet({ ...newSet, fractions: Number(e.target.value) })}
                  min={1}
                  required
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: 150 }}>
                <label>Description (optional)</label>
                <input
                  type="text"
                  value={newSet.description}
                  onChange={(e) => setNewSet({ ...newSet, description: e.target.value })}
                  placeholder="Brief description..."
                />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="btn-primary" disabled={creating}>
                  {creating ? 'Creating...' : 'Create'}
                </button>
                <button type="button" onClick={() => setShowCreate(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h2>
            {selectedSite
              ? `${bodySites.find((s) => s.id === selectedSite)?.name} Constraint Sets`
              : 'All Constraint Sets'}
          </h2>
          <div className="filter-bar" style={{ marginBottom: 0, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Search constraint sets..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showDeprecated}
                onChange={(e) => setShowDeprecated(e.target.checked)}
              />
              Show deprecated{deprecatedCount > 0 && ` (${deprecatedCount})`}
            </label>
            {(selectedSite || statusFilter !== 'all') && (
              <button className="btn-sm" onClick={() => { setSelectedSite(null); setStatusFilter('all'); }}>
                Clear filters
              </button>
            )}
            {user.can_edit_constraints && !showCreate && (
              <button className="btn-primary btn-sm" onClick={() => setShowCreate(true)}>
                + New Constraint Set
              </button>
            )}
            {user.can_edit_constraints && selectedSite !== null && (
              <button
                className="btn-sm"
                onClick={() => navigate(`/one-offs?body_site=${selectedSite}&new=1`)}
                title={`Create a one-off constraint scoped to ${bodySites.find((s) => s.id === selectedSite)?.name ?? 'this site'}`}
              >
                + One-Off for {bodySites.find((s) => s.id === selectedSite)?.name ?? 'site'}
              </button>
            )}
            {selectedForExport.size > 0 ? (
              <button className="btn-sm btn-primary" onClick={handleExportSelected} disabled={exporting}>
                {exporting ? 'Exporting...' : `Export ${selectedForExport.size} to XLSX`}
              </button>
            ) : (
              <button className="btn-sm" onClick={selectAllFiltered}>
                Select All for Export
              </button>
            )}
            {selectedForExport.size > 0 && user.can_edit_constraints && (
              <button
                className="btn-sm"
                onClick={() => setAssignReviewOpen(true)}
                title="Assign a reviewer to perform the annual reaffirmation on the selected sets"
              >
                Assign {selectedForExport.size} for review
              </button>
            )}
            {selectedForExport.size > 0 && (
              <button className="btn-sm" onClick={() => setSelectedForExport(new Set())}>
                Clear Selection
              </button>
            )}
          </div>
        </div>

        {/* Sort headers */}
        <div style={{ display: 'flex', padding: '8px 16px', borderBottom: '1px solid var(--color-border)', fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)' }}>
          <div style={{ width: 32 }}></div>
          <div
            style={{ flex: 1, cursor: 'pointer', userSelect: 'none' }}
            onClick={() => handleSort('name')}
          >
            Name{sortIndicator('name')}
          </div>
          <div
            style={{ width: 120, cursor: 'pointer', userSelect: 'none', textAlign: 'right' }}
            onClick={() => handleSort('status')}
          >
            Status{sortIndicator('status')}
          </div>
        </div>

        <div className="constraint-set-list">
          {sorted.length === 0 ? (
            <div className="empty-state">
              <h3>No constraint sets found</h3>
              <p>Try a different search or filter.</p>
            </div>
          ) : (
            Object.entries(grouped).map(([siteName, sets]) => (
              <div key={siteName}>
                <div style={{
                  padding: '8px 16px',
                  background: 'var(--color-bg-secondary, #f8f9fa)',
                  borderBottom: '1px solid var(--color-border)',
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: 'var(--color-text-secondary)',
                }}>
                  {siteName} ({sets.length})
                </div>
                {sets.map((cs) => (
                  <div
                    key={cs.id}
                    className="set-row"
                    style={{ cursor: 'pointer' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }} onClick={() => navigate(`/constraint-sets/${cs.id}`)}>
                      <input
                        type="checkbox"
                        checked={selectedForExport.has(cs.id)}
                        onChange={() => {}}
                        onClick={(e) => toggleExportSelect(cs.id, e)}
                        style={{ cursor: 'pointer' }}
                        title="Select for export"
                      />
                      <div>
                        <div className="set-name">{cs.name}</div>
                        <div className="set-meta">
                          {cs.fractions} fx
                          {cs.active_version_number && ` · v${cs.active_version_number}`}
                        </div>
                      </div>
                    </div>
                    <div className="set-badges">{getStatusBadges(cs)}</div>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>

      {/* v0.6.0.17: OneOffSets panel — only renders when sets exist.
          Clicking a card navigates to the OneOffSet detail page where
          the user downloads the RS script. */}
      {visibleOneOffSets.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-header">
            <h2>
              {selectedSite !== null
                ? `${bodySites.find((s) => s.id === selectedSite)?.name} One-Off Sets`
                : 'One-Off Sets'}
            </h2>
            <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
              Grouped ad-hoc constraints that ship to RayStation as one script.
            </span>
          </div>
          <div className="card-body" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {visibleOneOffSets.map((s) => (
              <div
                key={s.id}
                className="set-row"
                onClick={() => navigate(`/one-off-sets/${s.id}`)}
                style={{
                  cursor: 'pointer',
                  border: '1px solid var(--color-border)',
                  borderRadius: 6,
                  padding: '8px 12px',
                  minWidth: 220,
                  flex: '1 1 220px',
                }}
                title={`Open ${s.name}`}
              >
                <div className="set-name" style={{ fontWeight: 600 }}>{s.name}</div>
                <div className="set-meta" style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                  {selectedSite === null && `${s.body_site_name} · `}
                  {s.fractions} fx · {s.constraint_count} row{s.constraint_count !== 1 ? 's' : ''}
                  {s.owner_username && ` · ${s.owner_username}`}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {assignReviewOpen && (
        <AssignReviewModal
          selectedSets={constraintSets.filter((cs) => selectedForExport.has(cs.id))}
          onClose={() => setAssignReviewOpen(false)}
          onAssigned={() => {
            setAssignReviewOpen(false);
            setSelectedForExport(new Set());
            // Refresh the "assigned to you" widget — the editor may also
            // be the assignee (small team, common pattern), and the
            // widget should reflect the new pending row immediately.
            loadMyAssignments();
          }}
        />
      )}
    </div>
  );
}

interface ReviewsDueWidgetProps {
  state: ReviewsDueResponse | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}

function ReviewsDueWidget({ state, loading, error, onRetry }: ReviewsDueWidgetProps) {
  if (loading) {
    return (
      <div
        className="stat-card"
        role="region"
        aria-label="Constraint set review status"
        aria-busy="true"
      >
        <div
          className="stat-value"
          style={{ background: 'var(--color-border)', color: 'transparent', borderRadius: 4, width: 48 }}
        >
          0
        </div>
        <div
          className="stat-label"
          style={{ background: 'var(--color-border)', color: 'transparent', borderRadius: 4, marginTop: 4 }}
        >
          Loading
        </div>
      </div>
    );
  }

  if (error || state === null) {
    return (
      <div className="stat-card" role="region" aria-label="Constraint set review status">
        <div className="stat-label" style={{ color: 'var(--color-text-secondary)' }}>
          Could not load review status ·{' '}
          <button
            type="button"
            onClick={onRetry}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: 'var(--color-primary)',
              cursor: 'pointer',
              font: 'inherit',
              textDecoration: 'underline',
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { overdue, due_soon } = state.counts;
  const total = overdue + due_soon;

  // border-left color signals max severity (D4 from design review)
  let borderLeftColor: string | undefined;
  if (overdue > 0) borderLeftColor = 'var(--color-danger)';
  else if (due_soon > 0) borderLeftColor = 'var(--color-warning)';

  if (total === 0) {
    return (
      <div
        className="stat-card"
        role="region"
        aria-label="Annual review status"
        title="No constraint sets are overdue or due soon for their annual reaffirmation."
      >
        <div className="stat-value" aria-live="polite">0</div>
        <div className="stat-label">Reviews due</div>
        <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 2 }}>
          All current
        </div>
        {state.most_recent_review && (
          <div
            style={{
              fontSize: 11,
              color: 'var(--color-text-secondary)',
              marginTop: 4,
            }}
          >
            Most recent reaffirm:{' '}
            {state.most_recent_review.last_reviewed_at.slice(0, 10)}
            {state.most_recent_review.last_reviewed_by_name &&
              ` · ${state.most_recent_review.last_reviewed_by_name}`}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="stat-card"
      role="region"
      aria-label="Annual review status"
      title={
        overdue > 0
          ? `${overdue} constraint set${overdue === 1 ? '' : 's'} past the annual reaffirmation due date.`
          : `${due_soon} constraint set${due_soon === 1 ? '' : 's'} due for reaffirmation within the next 30 days.`
      }
      style={{
        borderLeft: borderLeftColor ? `4px solid ${borderLeftColor}` : undefined,
      }}
    >
      <div
        className="stat-value"
        aria-live="polite"
        style={{ color: overdue > 0 ? 'var(--color-danger)' : 'var(--color-warning)' }}
      >
        {overdue > 0 ? overdue : due_soon}
      </div>
      <div className="stat-label">
        {overdue > 0
          ? `Overdue${due_soon > 0 ? ` · ${due_soon} due soon` : ''}`
          : 'Due soon'}
      </div>
      <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 2 }}>
        Annual reaffirm
      </div>
    </div>
  );
}
